/** Every credential this account has ever issued, and the state of each.
 *
 * Revoked tokens are kept rather than deleted, because `last_used_at` on a
 * revoked token is the only record of what that credential did and when it
 * stopped, and the account most likely to want that record is the one that
 * revoked something in a hurry. But they are history, not the working list:
 * left in place they pile up under the live ones forever, so they fold
 * behind one toggle under the table and come out dimmed, with a tag instead
 * of a button — there is nothing left to do to them.
 *
 * "Last used" is coloured rather than annotated. A token used in the last day
 * is an installation that is working, which is what somebody scanning this
 * table is looking for; anything older is context and wears the neutral ramp.
 * A token never used at all says so, because an installation that has been
 * set up and has never spoken is a different problem from one that stopped.
 */

import { useState } from 'react';
import { EmptyState, Table, TBody, TD, TH, THead, TR, Tag } from '@/components/ui';
import { relativeTime, shortDate } from '@/lib/format';
import type { TokenSummary } from '@/lib/types';

export interface TokenTableProps {
  tokens: readonly TokenSummary[];
  onRevoke: (token: TokenSummary) => void;
  /** The id currently being revoked, so its row alone stops answering. */
  revoking?: string | null;
}

/** Within a day, an installation is alive; past that it is history. */
const RECENT_MS = 24 * 60 * 60 * 1000;

function usedColour(at: string | null): string {
  if (!at) return 'var(--color-neutral-600)';
  const used = Date.parse(at);
  if (Number.isNaN(used)) return 'var(--color-neutral-400)';
  return Date.now() - used < RECENT_MS ? 'var(--color-ok)' : 'var(--color-neutral-400)';
}

export function TokenTable({ tokens, onRevoke, revoking }: TokenTableProps) {
  const [showRevoked, setShowRevoked] = useState(false);

  if (tokens.length === 0) {
    return (
      <EmptyState title="no tokens yet" compact>
        A token is what one installation of the datalogger uses to reach this service. Make one on
        the right, then paste its connection string into the logger&rsquo;s Admin → Sync panel.
      </EmptyState>
    );
  }

  const live = tokens.filter((token) => token.revoked_at === null);
  const revoked = tokens.filter((token) => token.revoked_at !== null);
  const shown = showRevoked ? [...live, ...revoked] : live;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      {shown.length === 0 ? (
        <EmptyState title="no live tokens" compact>
          Every token this account made has been revoked. Make a new one on the right for each
          installation that should keep uploading.
        </EmptyState>
      ) : (
        <Table scroll label="Tokens">
          <THead>
            <TR>
              <TH>Label</TH>
              <TH>Scopes</TH>
              <TH>Created</TH>
              <TH>Last used</TH>
              <TH>
                <span className="sr-only">Revoke</span>
              </TH>
            </TR>
          </THead>
          <TBody>
            {shown.map((token) => (
              <TokenRow
                key={token.id}
                token={token}
                onRevoke={onRevoke}
                busy={revoking === token.id}
              />
            ))}
          </TBody>
        </Table>
      )}

      {revoked.length > 0 ? (
        <button
          type="button"
          className="btn btn-ghost"
          style={{ alignSelf: 'flex-start', fontSize: 12, color: 'var(--color-neutral-500)' }}
          aria-expanded={showRevoked}
          onClick={() => setShowRevoked((open) => !open)}
        >
          {showRevoked ? 'Hide' : 'Show'} {revoked.length} revoked
        </button>
      ) : null}
    </div>
  );
}

function TokenRow({
  token,
  onRevoke,
  busy,
}: {
  token: TokenSummary;
  onRevoke: (token: TokenSummary) => void;
  busy: boolean;
}) {
  const revoked = token.revoked_at !== null;
  return (
    <TR style={revoked ? { opacity: 0.55 } : undefined}>
      <TD>{token.label}</TD>
      <TD>
        <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
          {token.scopes.map((scope) => (
            <Tag key={scope} tone="neutral" className="font-mono text-[10.5px]">
              {scope}
            </Tag>
          ))}
        </span>
      </TD>
      <TD numeric color="var(--color-neutral-400)" align="left">
        {shortDate(token.created_at)}
      </TD>
      <TD numeric color={usedColour(token.last_used_at)} align="left">
        {token.last_used_at ? relativeTime(token.last_used_at) : 'never used'}
      </TD>
      <TD align="right">
        {revoked ? (
          <Tag tone="quiet" title={`revoked ${relativeTime(token.revoked_at)}`}>
            revoked
          </Tag>
        ) : (
          <button
            type="button"
            className="btn btn-ghost"
            style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}
            disabled={busy}
            onClick={() => onRevoke(token)}
          >
            {busy ? 'Revoking…' : 'Revoke'}
            <span className="sr-only">&nbsp;{token.label}</span>
          </button>
        )}
      </TD>
    </TR>
  );
}

export default TokenTable;
